import React, { useState } from 'react';
import { Network, Globe, ShieldCheck, Server, Activity, AlertTriangle, Clock } from 'lucide-react';

const NETWORK_BLUEPRINTS = [
  { id: 'net-vlan-provisioning', name: 'VLAN Provisioning & Segmentation', description: 'Automated VLAN creation, port assignment, SVI config, and firewall policy push across multi-vendor environments.', icon: Network, difficulty: 'intermediate', duration: '~5 min', category: 'VLAN', rating: 4.8, usageCount: 1089 },
  { id: 'net-ip-allocation-dns', name: 'IP Address Allocation & DNS', description: 'IP reservation from IPAM, forward/reverse DNS records, DHCP reservation for static hosts.', icon: Globe, difficulty: 'beginner', duration: '~1 min', category: 'IPAM', rating: 4.7, usageCount: 2134 },
  { id: 'net-firewall-rule-lifecycle', name: 'Firewall Rule Request & Lifecycle', description: 'Rule request, conflict/shadow check, AI risk assessment, change approval, multi-vendor push, periodic review.', icon: ShieldCheck, difficulty: 'advanced', duration: '~30 min', category: 'Firewall', rating: 4.9, usageCount: 784 },
  { id: 'net-network-device-onboarding', name: 'Network Device Onboarding', description: 'Switch/router/AP onboarding: hostname, management IP, NTP, SNMP, TACACS+, register in monitoring.', icon: Server, difficulty: 'intermediate', duration: '~10 min', category: 'Devices', rating: 4.6, usageCount: 456 },
];

const STATS = [
  { label: 'Managed Devices', value: '342', icon: Server, color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
  { label: 'VLANs', value: '187', icon: Network, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { label: 'FW Rules', value: '2,841', icon: ShieldCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { label: 'Alerts Today', value: '5', icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10' },
];

const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: 'bg-emerald-500/20 text-emerald-300',
  intermediate: 'bg-blue-500/20 text-blue-300',
  advanced: 'bg-purple-500/20 text-purple-300',
};

export function NetworkPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'VLAN', 'IPAM', 'Firewall', 'Devices'];
  const filtered = activeCategory === 'All' ? NETWORK_BLUEPRINTS : NETWORK_BLUEPRINTS.filter((b) => b.category === activeCategory);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
              <Network className="w-5 h-5 text-indigo-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Network</h1>
          </div>
          <p className="text-gray-500">VLAN management, IPAM, firewall rules, and device onboarding automation</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs text-emerald-300 font-medium">Connected</span>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {STATS.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center mb-3`}>
                <Icon className={`w-4 h-4 ${stat.color}`} />
              </div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-gray-400 mt-0.5">{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* Topology Summary */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <Activity className="w-4 h-4 text-gray-400" />
          Recent Network Automations
        </h3>
        <div className="space-y-2">
          {[
            { action: 'VLAN 120 provisioned – DEV-APP-NEW', time: '15 min ago', status: 'success' },
            { action: 'Firewall rule: allow 10.0.0.0/24 → 10.20.5.10:443', time: '1 hour ago', status: 'success' },
            { action: 'IP allocated: 10.10.50.45 → web-app-prod-03', time: '2 hours ago', status: 'success' },
            { action: 'Device onboarded: sw-floor3-b01 (Cisco 9300)', time: '5 hours ago', status: 'success' },
            { action: 'Expired FW rule review: 14 rules flagged', time: '1 day ago', status: 'warning' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-200 last:border-0">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.status === 'success' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
              <span className="text-sm text-white flex-1">{item.action}</span>
              <span className="text-xs text-gray-400">{item.time}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Automation Blueprints</h2>
          <div className="flex gap-2">
            {categories.map((cat) => (
              <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeCategory === cat ? 'bg-orbayx-600/30 text-orbayx-700 border border-orbayx-500/30' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((bp) => {
            const Icon = bp.icon;
            return (
              <div key={bp.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-indigo-500/30 transition-all group cursor-pointer">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/15 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-indigo-400" />
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${DIFFICULTY_COLORS[bp.difficulty]}`}>{bp.difficulty}</span>
                </div>
                <h3 className="text-sm font-semibold text-white mb-1 group-hover:text-indigo-300 transition-colors">{bp.name}</h3>
                <p className="text-xs text-gray-400 mb-3">{bp.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{bp.duration}</span>
                  <span>⭐ {bp.rating} · {bp.usageCount.toLocaleString()} runs</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
